import { ActionReducer } from "@ngrx/store";
import { localStorageSync } from "ngrx-store-localstorage";
import { RootState } from "../..";
import { persistedKeys as programPersistedKeys, PROGRAM_FEATURE_KEY } from "../../program/state";
import { environment } from "../../../../environments/environment";
import { APP_FEATURE_KEY, AppState, persistedKeys as appPersistedKeys } from "./../state";
import { SHOPIFY_HOST } from "../../../constants";
import { WalletParams } from "../../../interfaces";
import { getSafeStorage } from "../../../utils/safe-storage/safe-storage";
import { isDialogHostPath } from "../../../utils/dialog-host/dialog-host";

// ngrx-store-localstorage only applies the `appPersistedKeys` whitelist when *writing* to
// localStorage. On rehydrate it merges back whatever raw object is stored under the "app" key,
// so any key that was persisted by an older build (e.g. a since-removed "pages" entry) keeps
// resurfacing on every fresh load even though it is no longer in the whitelist. Explicitly
// filtering the rehydrated object closes that gap.
const deserializeAppState =
  (keys: (keyof AppState)[]) =>
  (raw: Partial<AppState> | null): Partial<AppState> => {
    const filtered: Record<string, unknown> = {};
    const source = raw as Record<string, unknown> | null;
    keys.forEach(key => {
      if (source && Object.prototype.hasOwnProperty.call(source, key)) filtered[key] = source[key];
    });
    return filtered;
  };

// Inside Shopify App Bridge hands out a fresh session token on every start (and request): it stays in memory only
const SESSION_KEYS: (keyof AppState)[] = ["accessToken", "userId"];

// The copy of the admin that Shopify loads inside a modal reads the saved state of the page behind it but never
// writes it back: its own navigation (lastRoute) and dialog-only changes must not replace what that page saved.
const readOnlyStorage = (storage: Storage): Storage => ({
  get length(): number {
    return storage.length;
  },
  clear: (): void => {},
  getItem: (key: string): string | null => storage.getItem(key),
  key: (index: number): string | null => storage.key(index),
  removeItem: (): void => {},
  setItem: (): void => {},
});

export const localStorageReduxSyncMetaReducer = (reducer: ActionReducer<RootState>): ActionReducer<void> => {
  let programSlug = "";

  // Convercus
  try {
    const { programId }: WalletParams = JSON.parse(window.name);
    if (programId) programSlug = programId;
  } catch {}

  // Shopify
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get("shop");

    if (shop) programSlug = shop?.replace(SHOPIFY_HOST, "");
    else programSlug = window.location.pathname.split("/").filter(Boolean).shift();
  } catch {}

  const appKeys = window.shopify?.environment?.embedded ? appPersistedKeys.filter(key => !SESSION_KEYS.includes(key)) : appPersistedKeys;

  return localStorageSync({
    keys: [
      { [APP_FEATURE_KEY]: { deserialize: deserializeAppState(appKeys), filter: appKeys } },
      { [PROGRAM_FEATURE_KEY]: programPersistedKeys },
    ],
    rehydrate: true,
    storage: isDialogHostPath() ? readOnlyStorage(getSafeStorage("localStorage")) : getSafeStorage("localStorage"),
    storageKeySerializer: key => `${programSlug}_${environment.name}_${key}`,
    restoreDates: true,
  })(reducer);
};
